'use client'
import React, { createContext, useContext } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  useRadio,
  useRadioGroup,
  type UseRadioProps,
  type UseRadioGroupProps,
  type BoxProps,
  type TextProps,
  Center,
  type StackProps
} from '@chakra-ui/react'

// Context for radio group state
interface RadioCardContextValue {
  size?: 'sm' | 'md' | 'lg'
  variant?: 'outline' | 'filled' | 'subtle'
  colorScheme?: string
  getRadioProps: (props: { value: string }) => UseRadioProps
}

const RadioCardContext = createContext<RadioCardContextValue>({
  getRadioProps: () => ({})
})

// Context for individual radio item state
interface RadioCardItemContextValue {
  isChecked: boolean
  isDisabled?: boolean
  isFocused: boolean
}

const RadioCardItemContext = createContext<RadioCardItemContextValue>({
  isChecked: false,
  isDisabled: false,
  isFocused: false
})

// Hook to access radio card context
export const useRadioCardContext = () => useContext(RadioCardContext)
export const useRadioCardItemContext = () => useContext(RadioCardItemContext)

// Size variants
const sizeVariants = {
  sm: {
    root: { p: 3, gap: 2 },
    text: { fontSize: 'sm' },
    description: { fontSize: 'xs' },
    indicator: { w: 4, h: 4 }
  },
  md: {
    root: { p: 4, gap: 3 },
    text: { fontSize: 'md' },
    description: { fontSize: 'sm' },
    indicator: { w: 5, h: 5 }
  },
  lg: {
    root: { p: 5, gap: 4 },
    text: { fontSize: 'lg' },
    description: { fontSize: 'md' },
    indicator: { w: 6, h: 6 }
  }
}

// Visual variants
const visualVariants = {
  outline: {
    borderWidth: '1px',
    borderColor: 'gray.200',
    bg: 'white',
    _checked: {
      borderColor: 'blue.500',
      bg: 'blue.50'
    },
    _hover: {
      borderColor: 'gray.300'
    }
  },
  filled: {
    borderWidth: '1px',
    borderColor: 'transparent',
    bg: 'gray.50',
    _checked: {
      borderColor: 'blue.500',
      bg: 'blue.500',
      color: 'white'
    },
    _hover: {
      bg: 'gray.100'
    }
  },
  subtle: {
    borderWidth: '0px',
    bg: 'gray.50',
    _checked: {
      bg: 'blue.100'
    },
    _hover: {
      bg: 'gray.100'
    }
  }
}

// Root component - manages radio group
export interface GrandRadioCardRootProps extends UseRadioGroupProps {
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  variant?: 'outline' | 'filled' | 'subtle'
  colorScheme?: string
  rootProps?: BoxProps
}

export const GrandRadioCardRoot: React.FC<GrandRadioCardRootProps> = ({
  children,
  size = 'md',
  variant = 'outline',
  colorScheme = 'blue',
  rootProps,
  ...radioGroupProps
}) => {
  const { getRootProps, getRadioProps } = useRadioGroup(radioGroupProps)

  return (
    <RadioCardContext.Provider value={{ size, variant, colorScheme, getRadioProps }}>
      <Box {...getRootProps()} {...rootProps}>
        {children}
      </Box>
    </RadioCardContext.Provider>
  )
}

// Label component
export type GrandRadioCardLabelProps = TextProps

export const GrandRadioCardLabel: React.FC<GrandRadioCardLabelProps> = ({ children, ...props }) => {
  const { size } = useRadioCardContext()
  const sizeStyles = sizeVariants[size || 'md']

  return (
    <Text fontWeight="semibold" color="gray.900" mb={2} {...sizeStyles.text} {...props}>
      {children}
    </Text>
  )
}

// Item component - individual radio card
export type GrandRadioCardItemProps = UseRadioProps &
  Omit<BoxProps, 'onChange'> & {
    value: string
  }

export const GrandRadioCardItem: React.FC<GrandRadioCardItemProps> = ({
  children,
  value,
  ...props
}) => {
  const { size, variant, getRadioProps: getRadioPropsFromContext } = useRadioCardContext()
  const radioPropsFromGroup = getRadioPropsFromContext({ value })

  const { getInputProps, getRadioProps, getLabelProps, state } = useRadio({
    // value,
    ...radioPropsFromGroup,
    ...props
  })

  const { isChecked, isDisabled, isFocused } = state
  const sizeStyles = sizeVariants[size || 'md']
  const variantStyles = visualVariants[variant || 'outline']

  const inputProps = getInputProps()

  return (
    <RadioCardItemContext.Provider value={{ isChecked, isDisabled, isFocused }}>
      <Box
        as="label"
        display="block"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        opacity={isDisabled ? 0.6 : 1}
        borderRadius="md"
        transition="all 0.2s"
        _focus={{
          boxShadow: 'outline'
        }}
        {...sizeStyles.root}
        {...variantStyles}
        {...getLabelProps()}
        {...getRadioProps()}
        {...props}
      >
        <input {...inputProps} />
        {children}
      </Box>
    </RadioCardItemContext.Provider>
  )
}

export type GrandRadioCardItemControlProps = StackProps

export const GrandRadioCardItemControl: React.FC<GrandRadioCardItemControlProps> = ({
  children,
  ...props
}) => {
  return (
    <HStack align="stretch" gap={4} w="100%" {...props}>
      {children}
    </HStack>
  )
}

// Indicator component - the radio button visual indicator
export interface GrandRadioCardIndicatorProps extends BoxProps {
  colorScheme?: string
}

export const GrandRadioCardIndicator: React.FC<GrandRadioCardIndicatorProps> = ({
  colorScheme,
  ...props
}) => {
  const { size, colorScheme: contextColorScheme } = useRadioCardContext()
  const { isChecked } = useRadioCardItemContext()
  const sizeStyles = sizeVariants[size || 'md']
  const activeColorScheme = colorScheme || contextColorScheme || 'blue'

  return (
    <Center
      borderRadius="full"
      borderWidth="1px"
      borderColor={`${activeColorScheme}.500`}
      flexShrink={0}
      _before={
        isChecked
          ? {
              content: '""',
              display: 'block',
              w: '50%',
              height: '50%',
              bg: `${activeColorScheme}.500`,
              borderRadius: 'full'
            }
          : {}
      }
      {...sizeStyles.indicator}
      {...props}
    />
  )
}

// Header component with indicator
export interface GrandRadioCardItemHeaderProps {
  children: React.ReactNode
  showIndicator?: boolean
  headerProps?: BoxProps
}

export const GrandRadioCardItemHeader: React.FC<GrandRadioCardItemHeaderProps> = ({
  children,
  showIndicator = true,
  headerProps
}) => {
  return (
    <HStack justify="space-between" align="flex-start" w="100%" {...headerProps}>
      <HStack align="flex-start" spacing={3} flex={1}>
        {showIndicator && <GrandRadioCardIndicator mt={0.5} />}
        <Box flex={1}>{children}</Box>
      </HStack>
    </HStack>
  )
}

// Text component
export type GrandRadioCardItemTextProps = TextProps

export const GrandRadioCardItemText: React.FC<GrandRadioCardItemTextProps> = ({
  children,
  ...props
}) => {
  const { size } = useRadioCardContext()
  const sizeStyles = sizeVariants[size || 'md']

  return (
    <Text fontWeight="semibold" color="gray.900" {...sizeStyles.text} {...props}>
      {children}
    </Text>
  )
}

// Description component
export type GrandRadioCardItemDescriptionProps = TextProps

export const GrandRadioCardItemDescription: React.FC<GrandRadioCardItemDescriptionProps> = ({
  children,
  ...props
}) => {
  const { size } = useRadioCardContext()
  const sizeStyles = sizeVariants[size || 'md']

  return (
    <Text color="gray.600" {...sizeStyles.description} {...props}>
      {children}
    </Text>
  )
}

// Content component - container for text and description
export type GrandRadioCardItemContentProps = StackProps

export const GrandRadioCardItemContent: React.FC<GrandRadioCardItemContentProps> = ({
  children,
  ...props
}) => {
  return (
    <VStack align="flex-start" gap={1} flex={1} {...props}>
      {children}
    </VStack>
  )
}

// Addon component - for additional content
export type GrandRadioCardItemAddonProps = BoxProps

export const GrandRadioCardItemAddon: React.FC<GrandRadioCardItemAddonProps> = ({
  children,
  ...props
}) => {
  const { size } = useRadioCardContext()
  const sizeStyles = sizeVariants[size || 'md']

  return (
    <Box pt={2} borderTopWidth="1px" borderColor="gray.100" {...sizeStyles.description} {...props}>
      {children}
    </Box>
  )
}

// Compound component export following Chakra v3 pattern
export const GrandRadioCard = {
  Root: GrandRadioCardRoot,
  Label: GrandRadioCardLabel,
  Item: GrandRadioCardItem,
  ItemControl: GrandRadioCardItemControl, // Deprecated - use VStack directly
  ItemHeader: GrandRadioCardItemHeader,
  ItemText: GrandRadioCardItemText,
  ItemDescription: GrandRadioCardItemDescription,
  ItemContent: GrandRadioCardItemContent,
  ItemAddon: GrandRadioCardItemAddon,
  Indicator: GrandRadioCardIndicator
}
